import React, { useEffect, useMemo, useState } from 'react';
import { Button, Grid, Header, Icon, Loader, Message, Modal, Segment } from 'semantic-ui-react';
import {
  ContractDocusignSendHistoryDto,
  ContractListDto,
  contractService,
} from '../../services/contracts/ContractService';
import {
  getContractTypeLabel,
  isAditivoContract,
  isRenegociacaoContract,
  sortContractsForVtmeGroupOrder,
} from '../../utils/contractGroupVtmeOrder';
import DocusignHistoryEntry from './DocusignHistoryEntry';

interface GroupDocusignSendHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  groupContracts: ContractListDto[];
}

type HistoryState = {
  loading: boolean;
  error: string | null;
  history: ContractDocusignSendHistoryDto[];
};

const emptyHistoryState: HistoryState = {
  loading: false,
  error: null,
  history: [],
};

const GroupDocusignSendHistoryModal: React.FC<GroupDocusignSendHistoryModalProps> = ({
  isOpen,
  onClose,
  groupContracts,
}) => {
  const [historyByContractId, setHistoryByContractId] = useState<Record<string, HistoryState>>({});

  const columns = useMemo(() => {
    const sorted = sortContractsForVtmeGroupOrder(groupContracts);
    const aditivo = sorted.find(isAditivoContract) ?? sorted[0] ?? null;
    const renegociacao =
      sorted.find(isRenegociacaoContract) ?? sorted.find((contract) => contract.id !== aditivo?.id) ?? null;

    return [
      { key: 'aditivo', title: 'Aditivo', contract: aditivo },
      { key: 'renegociacao', title: 'Renegociação', contract: renegociacao },
    ];
  }, [groupContracts]);

  const contractIds = columns.map((column) => column.contract?.id ?? '').join('|');

  useEffect(() => {
    if (!isOpen) return;

    const contractsToLoad = columns
      .map((column) => column.contract)
      .filter((contract): contract is ContractListDto => Boolean(contract));

    if (contractsToLoad.length === 0) {
      setHistoryByContractId({});
      return;
    }

    setHistoryByContractId(
      contractsToLoad.reduce<Record<string, HistoryState>>((acc, contract) => {
        acc[contract.id] = { ...emptyHistoryState, loading: true };
        return acc;
      }, {})
    );

    let cancelled = false;

    contractsToLoad.forEach(async (contract) => {
      try {
        const history = await contractService.getDocusignSendHistory(contract.id);
        if (cancelled) return;
        setHistoryByContractId((prev) => ({
          ...prev,
          [contract.id]: { loading: false, error: null, history },
        }));
      } catch (err: unknown) {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : 'Erro ao carregar histórico DocuSign';
        setHistoryByContractId((prev) => ({
          ...prev,
          [contract.id]: { loading: false, error: message, history: [] },
        }));
      }
    });

    return () => {
      cancelled = true;
    };
  }, [isOpen, contractIds, columns]);

  return (
    <Modal open={isOpen} onClose={onClose} size="large" closeIcon>
      <Modal.Header>
        <Icon name="file alternate" />
        Histórico completo DocuSign do grupo
      </Modal.Header>

      <Modal.Content scrolling>
        <Grid columns={2} divided stackable>
          {columns.map(({ key, title, contract }) => {
            const state = contract ? historyByContractId[contract.id] ?? emptyHistoryState : emptyHistoryState;

            return (
              <Grid.Column key={key}>
                <Segment style={{ minHeight: '420px' }}>
                  <Header size="medium">
                    <Icon name={key === 'aditivo' ? 'plus circle' : 'refresh'} />
                    <Header.Content>
                      {title}
                      {contract && (
                        <Header.Subheader>
                          {getContractTypeLabel(contract)} · ID {contract.id}
                        </Header.Subheader>
                      )}
                    </Header.Content>
                  </Header>

                  {!contract ? (
                    <Message info>
                      <Icon name="info circle" />
                      Nenhum contrato de {title} neste grupo.
                    </Message>
                  ) : state.loading ? (
                    <div style={{ padding: '32px', textAlign: 'center' }}>
                      <Loader active inline />
                      <div style={{ marginTop: '12px', color: '#666' }}>Carregando histórico...</div>
                    </div>
                  ) : state.error ? (
                    <Message negative>
                      <Icon name="warning circle" />
                      {state.error}
                    </Message>
                  ) : state.history.length === 0 ? (
                    <Message info>
                      <Icon name="info circle" />
                      Nenhum envio DocuSign registrado.
                    </Message>
                  ) : (
                    state.history.map((item, index) => (
                      <DocusignHistoryEntry
                        key={item.id}
                        item={item}
                        index={index}
                        isLast={index === state.history.length - 1}
                      />
                    ))
                  )}
                </Segment>
              </Grid.Column>
            );
          })}
        </Grid>
      </Modal.Content>

      <Modal.Actions>
        <Button onClick={onClose} primary>
          <Icon name="check" />
          Fechar
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default GroupDocusignSendHistoryModal;
